import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

/** Element en DOM van een geselecteerde, niet-versleepbare node waar het event in valt. */
function lockedSelectionHit(view: EditorView, target: EventTarget | null) {
  const { selection } = view.state;
  if (!(selection instanceof NodeSelection) || selection.node.type.spec.draggable) return null;
  const element =
    target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  const dom = view.nodeDOM(selection.from);
  return element && dom instanceof Element && dom.contains(element) ? { element, dom } : null;
}

/** Logo's en achtergronden zijn gewone <img>'s; afbeeldingen in de tekst sleep je via hun wrapper. */
function blockImageDrag(event: DragEvent) {
  if (event.target instanceof HTMLImageElement) event.preventDefault();
}

/**
 * Pagina's verplaats je in de paginalijst. ProseMirror sleept een geselecteerde node
 * echter zodra je erin klikt en de muis beweegt, ook als de node niet draggable is:
 * tekst selecteren in een geselecteerde pagina versleepte dan de hele pagina. En de
 * browser sleepte logo's en achtergronden als losse afbeelding de offerte in.
 */
export const QuotePageDrag = Extension.create({
  name: 'quotePageDrag',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        // Nodeviews houden drags van hun eigen elementen bij ProseMirror weg; vang ze vooraf.
        view: (view) => {
          view.dom.addEventListener('dragstart', blockImageDrag, true);
          return { destroy: () => view.dom.removeEventListener('dragstart', blockImageDrag, true) };
        },
        props: {
          handleDOMEvents: {
            mousedown: (view, event) => {
              const hit = event.button === 0 ? lockedSelectionHit(view, event.target) : null;
              const content = hit?.element.closest('[data-node-view-content]');
              if (!hit || !content || !hit.dom.contains(content)) return false;
              // Klik in de inhoud: eerst de cursor op het klikpunt, zodat de muis tekst selecteert.
              const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
              if (pos) {
                view.dispatch(
                  view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos.pos)))
                );
              }
              return false;
            },
            dragstart: (view, event) => {
              if (!lockedSelectionHit(view, event.target)) return false;
              event.preventDefault();
              return true;
            },
          },
        },
      }),
    ];
  },
});
